import { marked } from "marked";
import {
  ISocialPrompt,
  MESSAGE_FORMAT,
  SOCIAL_CONTENT_TYPE,
} from "../Types/account";
import {
  buildPlainClipboardPayload,
  copyPlainTextToClipboard,
  copyRichTextToClipboard,
  type RichClipboardCopyResult,
  type RichClipboardPayload,
} from "./clipboardRichText";

export function isMarkdownFormat(format: MESSAGE_FORMAT): boolean {
  return format === MESSAGE_FORMAT.MARKDOWN;
}

/** Decode entities left in generated plain text without interpreting its HTML tags. */
export function decodePromptText(value: string): string {
  if (
    !/&(?:#\d+|#x[\da-f]+|[a-z][\da-z]+);/i.test(value) ||
    typeof document === "undefined"
  ) {
    return value;
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return textarea.value;
}

export function getPromptMessageFieldLabel(
  contentType: SOCIAL_CONTENT_TYPE | null | undefined,
  format?: MESSAGE_FORMAT
): string {
  if (
    contentType === SOCIAL_CONTENT_TYPE.BLOG &&
    format != null &&
    isMarkdownFormat(format)
  ) {
    return "Blog draft";
  }
  return "Message";
}

/** Content types that always expose a subject field in compose/read UIs. */
export function promptSupportsSubjectField(
  contentType: SOCIAL_CONTENT_TYPE | null | undefined
): boolean {
  return (
    contentType === SOCIAL_CONTENT_TYPE.EMAIL ||
    contentType === SOCIAL_CONTENT_TYPE.LINKEDIN_DM
  );
}

/** Show subject in read UIs: supported types, or any prompt that already has one (e.g. BLOG). */
export function shouldShowPromptSubject(
  contentType: SOCIAL_CONTENT_TYPE | null | undefined,
  subject: string | null | undefined
): boolean {
  return (
    promptSupportsSubjectField(contentType) ||
    Boolean((subject ?? "").trim())
  );
}

/** Include subject when copying (EMAIL, LINKEDIN_DM, BLOG drafts). */
export function shouldIncludeSubjectInClipboard(
  contentType: SOCIAL_CONTENT_TYPE | null | undefined
): boolean {
  return (
    promptSupportsSubjectField(contentType) ||
    contentType === SOCIAL_CONTENT_TYPE.BLOG
  );
}

/** Clipboard text for social prompts — matches the "Copy Prompt" button formatting. */
export function formatSocialPromptClipboardText(prompt: ISocialPrompt): string | null {
  const text = [prompt.subject ? `Subject: ${prompt.subject}` : null, prompt.message]
    .filter(Boolean)
    .join("\n\n");
  const trimmed = text.trim();
  return trimmed ? text : null;
}

const EMAIL_HEADING_STYLES: Record<string, string> = {
  h1: "margin:0 0 12px;font-size:20px;font-weight:700;line-height:1.3;color:#0f172a;",
  h2: "margin:16px 0 8px;font-size:17px;font-weight:700;line-height:1.35;color:#0f172a;",
  h3: "margin:14px 0 6px;font-size:15px;font-weight:700;line-height:1.35;color:#0f172a;",
  h4: "margin:12px 0 6px;font-size:14px;font-weight:700;line-height:1.35;color:#0f172a;",
};

const EMAIL_BODY_STYLES = {
  p: "margin:0 0 10px;font-size:13px;line-height:1.5;color:#334155;",
  ul: "margin:0 0 12px;padding-left:22px;font-size:13px;line-height:1.5;color:#334155;",
  ol: "margin:0 0 12px;padding-left:22px;font-size:13px;line-height:1.5;color:#334155;",
  li: "margin:0 0 4px;",
  blockquote:
    "margin:0 0 12px;padding:8px 12px;border-left:3px solid #cbd5e1;color:#475569;font-size:13px;line-height:1.5;",
  pre: "margin:0 0 12px;padding:10px 12px;background:#f8fafc;border-radius:8px;font-size:12px;line-height:1.45;overflow:auto;",
  code: "font-family:Consolas,Monaco,monospace;font-size:12px;",
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Strip risky tags/attrs before clipboard paste into mail clients. */
export function sanitizeClipboardHtml(html: string): string {
  if (typeof DOMParser === "undefined") {
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/\son\w+='[^']*'/gi, "");
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("script,iframe,object,embed,link,meta")
    .forEach((el) => el.remove());
  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (/^on/i.test(attr.name) || attr.name === "srcdoc") {
        el.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML;
}

function applyEmailInlineStyles(html: string): string {
  let next = html;
  for (const [tag, style] of Object.entries(EMAIL_HEADING_STYLES)) {
    next = next.replace(
      new RegExp(`<${tag}(\\s[^>]*)?>`, "gi"),
      `<${tag} style="${style}">`
    );
  }
  for (const [tag, style] of Object.entries(EMAIL_BODY_STYLES)) {
    next = next.replace(
      new RegExp(`<${tag}(\\s[^>]*)?>`, "gi"),
      `<${tag} style="${style}">`
    );
  }
  return next;
}

/** Convert markdown source into email-friendly HTML for rich clipboard copy. */
export function markdownToClipboardHtml(markdown: string): string {
  const parsed = marked.parse(markdown, { async: false });
  const rawHtml = typeof parsed === "string" ? parsed : String(parsed);
  const styled = applyEmailInlineStyles(sanitizeClipboardHtml(rawHtml));
  return `<div style="font-family:Segoe UI,Arial,sans-serif;color:#0f172a;">${styled}</div>`;
}

export function buildSocialPromptClipboard(input: {
  subject?: string | null;
  message?: string | null;
  messageFormat?: MESSAGE_FORMAT | null;
}): RichClipboardPayload | null {
  const format = input.messageFormat ?? MESSAGE_FORMAT.TEXT;
  const subject = decodePromptText(input.subject?.trim() || "");
  const rawMessage = input.message?.trim() || "";
  const message = isMarkdownFormat(format) ? rawMessage : decodePromptText(rawMessage);
  if (!subject && !message) return null;

  const text = [subject ? `Subject: ${subject}` : null, message || null]
    .filter(Boolean)
    .join("\n\n");

  if (isMarkdownFormat(format) && message) {
    const subjectHtml = subject
      ? `<p style="margin:0 0 12px;font-size:13px;line-height:1.5;color:#334155;"><strong>Subject:</strong> ${escapeHtml(
          subject
        )}</p>`
      : "";
    const bodyHtml = markdownToClipboardHtml(message);
    // bodyHtml already wraps in a div — nest subject above inside one wrapper
    const html = subjectHtml
      ? `<div style="font-family:Segoe UI,Arial,sans-serif;color:#0f172a;">${subjectHtml}${bodyHtml.replace(
          /^<div[^>]*>/,
          ""
        ).replace(/<\/div>$/, "")}</div>`
      : bodyHtml;
    return { text, html };
  }

  return buildPlainClipboardPayload(text);
}

/** Build rich clipboard payload from a social prompt (uses live subject/message when provided). */
export function buildSocialPromptClipboardFromPrompt(
  prompt: Pick<ISocialPrompt, "subject" | "message" | "message_format">,
  overrides?: { subject?: string | null; message?: string | null }
): RichClipboardPayload | null {
  return buildSocialPromptClipboard({
    subject: overrides?.subject ?? prompt.subject,
    message: overrides?.message ?? prompt.message,
    messageFormat: prompt.message_format ?? MESSAGE_FORMAT.TEXT,
  });
}

/** LinkedIn gets only text/plain; email and blog drafts keep rich formatting. */
export function copySocialPromptToClipboard(
  payload: RichClipboardPayload,
  contentType: SOCIAL_CONTENT_TYPE | null | undefined
): Promise<RichClipboardCopyResult> {
  return contentType === SOCIAL_CONTENT_TYPE.LINKEDIN_POST ||
    contentType === SOCIAL_CONTENT_TYPE.LINKEDIN_DM
    ? copyPlainTextToClipboard(payload.text)
    : copyRichTextToClipboard(payload);
}
